import { createApp, computed } from 'vue';
import { api } from './api.js';
import { state, setProject } from './state.js';
import { addRoute, matched, route, navigate, RouterLink } from './router.js';
import { pinia } from './store.js';
import { loadUserData } from './bootstrap.js';

import { Login } from './pages/Login.js';
import { Projects } from './pages/Projects.js';
import { Project } from './pages/Project.js';
import { Interview } from './pages/Interview.js';
import { Session } from './pages/Session.js';
import { TalkPage } from './pages/TalkPage.js';

/**
 * Заглушка для старых адресов: уводит в раздел выбранного проекта, а если он
 * не выбран — в список проектов.
 */
const Redirect = (section) => ({
  created() {
    const id = state.projectId;
    navigate(id ? `/project/${id}/${section}` : '/projects', true);
  },
  template: '<div class="page"><p class="muted">Перехожу…</p></div>',
});
import { Settings } from './pages/Settings.js';
import { Admin } from './pages/Admin.js';

addRoute('/', 'home', Projects);
addRoute('/projects', 'projects', Projects);
addRoute('/project/:id', 'project', Project);
// Повторы, прогресс и материалы — это про конкретный проект, а не про всё
// приложение, поэтому и адреса у них внутри проекта.
addRoute('/project/:id/cards', 'project-cards', Project);
addRoute('/project/:id/progress', 'project-progress', Project);
addRoute('/project/:id/materials', 'project-materials', Project);
addRoute('/project/:id/setup', 'project-setup', Project);
addRoute('/interview/:id', 'interview', Interview);
addRoute('/session/:id', 'session', Session);
// Обсуждение с учителем — свой экран: разговор в панели под разбором был тесен,
// поле ввода уезжало за край, а страница под руками скакала.
addRoute('/talk/:id', 'talk', TalkPage);
// Старые общие адреса: уводят в тот же раздел выбранного проекта. Их успели
// сохранить, и молча отдавать по ним пустоту — хуже, чем перенаправить.
addRoute('/progress', 'progress', Redirect('progress'));
addRoute('/cards', 'cards', Redirect('cards'));
// У каждого раздела настроек свой адрес: ссылку на любой из них можно дать
// как есть. Голые /settings и старый /agent ведут к агенту — за ним ходят
// разбираться, тему выбирают однажды.
addRoute('/admin', 'admin', Admin);
addRoute('/settings', 'settings', Settings);
addRoute('/settings/agent', 'settings-agent', Settings);
addRoute('/settings/limits', 'settings-limits', Settings);
addRoute('/settings/theme', 'settings-theme', Settings);
addRoute('/settings/projects', 'settings-projects', Settings);
addRoute('/agent', 'agent', Settings);

/**
 * Чем страница отличается от страницы.
 *
 * Ключ по полному адресу пересоздавал компонент на каждой смене пути — а
 * вкладки внутри страницы это тоже смена пути. Отсюда мигание и повторные
 * запросы при каждом клике по вкладке: страница честно рождалась заново, и
 * никакие v-show внутри неё этого изменить не могли.
 *
 * Ключ теперь — сам компонент плюс его :id. Вкладки одного проекта остаются
 * одной и той же страницей, а другой проект — другой, и его данные не
 * подмешаются к прежнему.
 */
const pageIds = new WeakMap();
let pageSeq = 0;
function keyFor(component, id) {
  if (!pageIds.has(component)) pageIds.set(component, (pageSeq += 1));
  return `${pageIds.get(component)}:${id || ''}`;
}

const App = {
  // Login обязателен в списке: он используется в шаблоне по имени, а импорт
  // сам по себе компонент не регистрирует — Vue его просто не найдёт и
  // отрисует пустоту. Остальные страницы подставляются через <component :is>
  // объектом, им регистрация не нужна.
  components: { RouterLink, Login },
  data() { return { booted: false, warning: '', runnerTimer: null }; },
  computed: {
    // Собес занимает весь экран: шапка приложения там мешает.
    bare() {
      return route.path.startsWith('/interview/') || route.path.startsWith('/talk/');
    },
    page() { return matched.value?.component || Projects; },
    user() { return state.user; },
    impersonating() { return state.impersonating; },
    pageKey() {
      const m = matched.value;
      return m ? keyFor(m.component, m.params.id) : 'none';
    },
    active() { return state.activeSession; },

    // В удалённом режиме агент живёт на машине человека. Если раннер не
    // запущен, собес не начнётся — и сказать об этом надо заранее, а не после
    // пятиминутного ожидания.
    runnerDown() { return state.runner.required && !state.runner.online; },
  },
  async created() {
    await this.boot();
    // Раннер могли выключить прямо во время работы: проверяем изредка.
    this.runnerTimer = setInterval(() => this.pollRunner(), 30000);
  },
  beforeUnmount() { clearInterval(this.runnerTimer); },
  methods: {
    async boot() {
      try {
        const s = await api.get('/api/auth/state');
        state.user = s.user;
        state.impersonating = Boolean(s.impersonating);
        state.needSetup = s.needSetup;
        state.inviteRequired = Boolean(s.inviteRequired);
        state.activeSession = s.activeSession;
        if (s.runner) state.runner = s.runner;
      } catch (e) {
        this.warning = 'Сервер не отвечает: ' + e.message;
      }
      if (state.user) {
        await loadUserData({ force: true });
        if (route.path === '/') navigate('/projects', true);
      }
      this.booted = true;
    },

    async pollRunner() {
      if (!state.user || !state.runner.required) return;
      try {
        state.runner = { ...state.runner, ...await api.get('/api/runner/state') };
      } catch { /* сеть моргнула — покажем на следующем круге */ }
    },

    /** Выйти из чужого аккаунта обратно в свой. */
    async returnToSelf() {
      await api.post('/api/admin/return', {});
      const s = await api.get('/api/auth/state');
      state.user = s.user;
      state.impersonating = false;
      state.activeSession = s.activeSession;
      await loadUserData({ force: true });
      navigate('/admin');
    },

    async logout() {
      await api.post('/api/auth/logout');
      state.user = null;
      state.projects = [];
      state.activeSession = null;
      setProject(null);
      // Перечитываем состояние: после выхода могло не остаться ни одного
      // пользователя, и форма должна предложить регистрацию.
      const s = await api.get('/api/auth/state').catch(() => ({ needSetup: false }));
      state.needSetup = s.needSetup;
      navigate('/projects');
    },
  },
  template: `
    <div v-if="!booted" class="empty">Загружаюсь…</div>

    <Login v-else-if="!user" />

    <component v-else-if="bare" :is="page" :key="pageKey" />

    <div v-else class="shell">
      <div class="topbar">
        <div class="brand">Мок-собес <span>·  тренажёр</span></div>
        <nav class="nav">
          <router-link to="/projects">Проекты</router-link>

        </nav>
        <span class="spacer"></span>
        <router-link :to="'/interview/' + active.id" v-if="active" class="active-session">
          <span class="tag warn">идёт собес — вернуться</span>
        </router-link>
        <router-link to="/admin" class="gear" v-if="user.is_admin && !impersonating"
                     title="Администратор" aria-label="Администратор">
          <svg viewBox="0 0 24 24" width="19" height="19" fill="none"
               stroke="currentColor" stroke-width="1.7"
               stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 3l7 3v5c0 4.2-2.9 7.9-7 9-4.1-1.1-7-4.8-7-9V6l7-3z"></path>
            <path d="M9.5 12l1.8 1.8L15 10"></path>
          </svg>
        </router-link>
        <router-link to="/settings" class="gear" title="Настройки" aria-label="Настройки">
          <svg viewBox="0 0 24 24" width="19" height="19" fill="none"
               stroke="currentColor" stroke-width="1.7"
               stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="3.1"></circle>
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9v.09a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
          </svg>
        </router-link>
        <span class="faint">{{ user.login }}</span>
        <button class="ghost sm" @click="logout">выйти</button>
      </div>

      <div class="banner as-user" v-if="impersonating">
        <strong>Ты в аккаунте {{ user.login }}.</strong>
        Всё, что здесь происходит, происходит от его имени.
        <button class="ghost sm" @click="returnToSelf">Вернуться к себе</button>
      </div>

      <p class="err" v-if="warning" style="padding:12px 24px">{{ warning }}</p>

      <div class="banner" v-if="runnerDown">
        <strong>Агент не на связи.</strong>
        Собеседование проводит codex или claude под твоей подпиской — сервер их
        запустить не может. Где он работает и что с ним сейчас, видно в
        <router-link to="/settings/agent">настройках</router-link>.
      </div>
      <component :is="page" :key="pageKey" />
    </div>`,
};

// Стор подключаем к приложению, хотя экземпляры уже созданы: так он виден
// в devtools и доступен компонентам через inject, а не только импортом.
createApp(App).use(pinia).mount('#app');
